import { Button } from "@/atoms-react/button/Button.react";
import Image from "@/atoms-react/image/Image.react";
import { Base } from "@/atoms-react/text/Text.react";
import { LegalDocument } from "@/features/user/document/types";
import { Themes } from "@/types/theme";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

const MAX_FILE_SIZE = 2 * 1024 * 1024;

export type propsType = {
  onChange: (data: LegalDocument) => void;
  onError?: (messageKey: string) => void;
  document: LegalDocument | null;
  className?: string;
  loadingRequest?: boolean;
  disabled?: boolean;
  dataCy?: string;
};

export const UploadDocumentInput = ({
  className,
  document,
  onChange,
  onError,
  loadingRequest = false,
  disabled = false,
  dataCy: dataCypress
}: propsType) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => event.preventDefault();
  const [loading, setLoading] = useState<boolean>(loadingRequest);
  const [uploaded, setUploaded] = useState<LegalDocument | null>(document);
  const { t } = useTranslation(["pass"]);

  useEffect(() => {
    if (document) setUploaded(document);
  }, [document]);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (!event?.dataTransfer?.files) return;
    onUpload(event.dataTransfer.files);
  };
  const onUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || loading) {
      return;
    }
    setLoading(true);
    const toBase64 = (file: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = (error) => reject(error);
      });
    const base64 = await toBase64(files[0]);

    if (base64.length >= MAX_FILE_SIZE) {
      if (onError) onError("atoms:molecules.upload_document_input.error.document_too_big");
      setLoading(false);
      return;
    }

    try {
      await onChange({
        base64docs: base64,
        name: files[0].name,
        fileFormat: files[0].type
      });
      setUploaded({
        base64docs: base64,
        name: files[0].name,
        fileFormat: files[0].type
      });
      setLoading(false);
    } catch (_) {
      setLoading(false);
    }
  };

  return (
    <div
      className={twMerge(
        "flex h-auto flex-col items-center justify-between rounded-sm border-2 border-slate-100 md:flex-row",
        "w-full rounded-sm border-2 border-slate-50 px-1 py-1 font-semibold text-black duration-300 placeholder:font-semibold placeholder:text-slate-400 focus:border-primary focus:bg-white dark:border-slate-800 dark:bg-slate-800 dark:text-white dark:focus:border-primary dark:focus:bg-slate-900 dark:placeholder:text-slate-600",
        !uploaded || uploaded === null ? "bg-slate-50" : "",
        className
      )}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <>
        {!uploaded || uploaded === null ? (
          <>
            <div className="flex w-full flex-col justify-between p-3 sm:flex-row sm:p-0">
              <div className="flex w-full flex-row justify-end">
                <Button
                  className="w-32 rounded-lg border-primary bg-primary"
                  disabled={disabled}
                  loading={loading}
                  onClick={() => {
                    if (!inputRef.current) return;
                    inputRef.current.click();
                  }}
                >
                  <Base className="text-center text-sm text-white">
                    {t("aliasDocumentsPage.deposit")}{" "}
                  </Base>
                  <input
                    data-cy={dataCypress}
                    type="file"
                    accept="image/png, image/jpeg, application/pdf"
                    disabled={disabled}
                    onChange={(event) => {
                      if (disabled) return;
                      onUpload(event.target.files);
                    }}
                    hidden
                    ref={inputRef}
                  ></input>
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex w-full flex-row justify-between p-3 sm:flex-row sm:p-0">
              <div className="flex sm:justify-center">
                <Image alt="pdf" className="h-10 w-10" src="/images/olkypass/pdf_icon.png" />
                <div
                  className="ml-2 flex cursor-pointer flex-col justify-center"
                  onClick={() => {
                    const pdfData =
                      uploaded.base64docs.slice(0, 4) !== "data"
                        ? `data:${uploaded.fileFormat};base64,${uploaded.base64docs}`
                        : `${uploaded.base64docs}`;

                    const w = window.open("");
                    if (w) {
                      w.document.body.innerHTML = `<embed width="100%" height="100%" src="${pdfData}"  />`;
                    } else {
                      throw new Error("Failed to open a new tab.");
                    }
                  }}
                >
                  <Base className="text-sm underline"> {uploaded.name}</Base>
                </div>
              </div>
              <div className="flex flex-row justify-end">
                <Button
                  className="w-32 rounded-lg border-primary"
                  loading={loading}
                  disabled={disabled}
                  onClick={() => {
                    if (!inputRef.current) return;
                    inputRef.current.click();
                  }}
                  theme={Themes.white}
                >
                  <Base className="text-center text-primary text-sm">
                    {t("aliasDocumentsPage.button_replace")}{" "}
                  </Base>
                  <input
                    data-cy={dataCypress}
                    type="file"
                    disabled={disabled}
                    onChange={(event) => {
                      onUpload(event.target.files);
                    }}
                    hidden
                    ref={inputRef}
                  ></input>
                </Button>
              </div>
            </div>
          </>
        )}
      </>
    </div>
  );
};
